import { useEffect, useMemo, useState } from 'react';
import ReactQuill from 'react-quill';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';

import { dispatchSingleAnswerMailAPI } from '../../../services/sendInvitation.service';
import client from '../../../utils/axiosInstance';

const STATUS_TABS = [
  { label: 'Todos', value: 'all' },
  { label: 'Respondieron', value: 'responded' },
  { label: 'No respondieron', value: 'pending' },
];

const getStatusChipStyles = (responded) =>
  responded
    ? {
        label: 'Respondió',
        sx: {
          bgcolor: '#e7f5ec',
          color: '#176c37',
          borderColor: '#9bd3ad',
          fontWeight: 700,
        },
      }
    : {
        label: 'No respondió',
        sx: {
          bgcolor: '#fdeaea',
          color: '#b42318',
          borderColor: '#f3a7a3',
          fontWeight: 700,
        },
      };

const emptyForm = {
  cedula: '',
  email: '',
  celular: '',
  indicativo: '',
  uidEmpresa: '',
  uid2: '',
  uid3: '',
};

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const emptyInvitationForm = {
  emailMask: '',
  emailSubject: '',
  message: '',
};

const normalizeValue = (value) => value ?? '';

const buildForm = (respondent) => ({
  cedula: normalizeValue(respondent?.cedula),
  email: normalizeValue(respondent?.email),
  celular: normalizeValue(respondent?.celular),
  indicativo: normalizeValue(respondent?.indicativo),
  uidEmpresa: normalizeValue(respondent?.uidEmpresa),
  uid2: normalizeValue(respondent?.uid2),
  uid3: normalizeValue(respondent?.uid3),
});

const getPrimaryIdentifier = (row) =>
  row.email ||
  row.mail ||
  row.cedula ||
  row.celular ||
  row.uidEmpresa ||
  row.answerId ||
  'Sin identificador';

const buildExcelRow = (row) => ({
  AnswerId: row.answerId || '',
  'Correo / identificador': getPrimaryIdentifier(row),
  Documento: row.cedula || '',
  Celular: [row.indicativo, row.celular].filter(Boolean).join(' '),
  'UID empresa': row.uidEmpresa || '',
  Uid2: row.uid2 || '',
  Uid3: row.uid3 || '',
  Estado: row.responded ? 'Respondió' : 'No respondió',
});

const hasAdminRole = () => {
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const roles = Array.isArray(userInfo?.role) ? userInfo.role : [userInfo?.role];

  return roles.includes('Administrador');
};

const createRequestId = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const getPlainText = (value = '') => value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

const PLACEHOLDER_RE = /@[^@]+@/;

const pickSurveyInvitationDefaults = (surveyData = {}) => {
  const response = surveyData.response || surveyData.Response || surveyData;

  return {
    emailMask:
      response.emailMAsk ||
      response.emailMask ||
      response.EmailMAsk ||
      response.EmailMask ||
      '',
    emailSubject:
      response.emailSubject ||
      response.EmailSubject ||
      '',
    message:
      response.emailMessage ||
      response.EmailMessage ||
      response.messageMail ||
      response.MessageMail ||
      '',
  };
};

export default function RespondentsDialog({
  open,
  onClose,
  surveyId,
  companyId,
  invitationDefaults = emptyInvitationForm,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rows, setRows] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalResponded, setTotalResponded] = useState(0);
  const [totalPending, setTotalPending] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [inviteRow, setInviteRow] = useState(null);
  const [invitationForm, setInvitationForm] = useState(emptyInvitationForm);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [exporting, setExporting] = useState(false);

  const canRunDestructiveActions = useMemo(hasAdminRole, []);

  const statusCountsLabel = useMemo(() => {
    if (status === 'all') return `${totalItems} registros`;
    return `${totalItems} ${status === 'responded' ? 'respondieron' : 'pendientes'}`;
  }, [status, totalItems]);

  const fetchRespondents = async () => {
    if (!open || !surveyId) return;

    setLoading(true);
    try {
      const response = await client.get(`surveys/${surveyId}/respondents`, {
        params: {
          status,
          search,
          page: page + 1,
          pageSize: rowsPerPage,
        },
      });

      setRows(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
      setTotalResponded(response.data?.totalResponded || 0);
      setTotalPending(response.data?.totalPending || 0);
    } catch (error) {
      enqueueSnackbar('No se pudieron cargar los respondentes', {
        variant: 'error',
        autoHideDuration: 2500,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(fetchRespondents, 350);
    return () => clearTimeout(timeoutId);
  }, [open, surveyId, status, search, page, rowsPerPage]);

  useEffect(() => {
    if (!open) {
      setStatus('all');
      setSearch('');
      setPage(0);
      setRows([]);
      setTotalItems(0);
      setTotalResponded(0);
      setTotalPending(0);
      setEditRow(null);
      setForm(emptyForm);
      setInviteRow(null);
      setInvitationForm(emptyInvitationForm);
    }
  }, [open]);

  const handleStatusChange = (event, newStatus) => {
    setStatus(newStatus);
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEdit = (row) => {
    setEditRow(row);
    setForm(buildForm(row));
  };

  const handleCloseEdit = () => {
    setEditRow(null);
    setForm(emptyForm);
  };

  const getCurrentSurveyInvitationDefaults = async () => {
    try {
      if (!surveyId || !companyId) return invitationDefaults;

      const response = await client.get(`ShowQuestion/${surveyId}/${companyId}`);
      const surveyDefaults = pickSurveyInvitationDefaults(response.data);

      return {
        emailMask: surveyDefaults.emailMask || invitationDefaults.emailMask || '',
        emailSubject: surveyDefaults.emailSubject || invitationDefaults.emailSubject || '',
        message: surveyDefaults.message || invitationDefaults.message || '',
      };
    } catch (error) {
      return {
        emailMask: invitationDefaults.emailMask || '',
        emailSubject: invitationDefaults.emailSubject || '',
        message: invitationDefaults.message || '',
      };
    }
  };

  const openInvitationDialog = async (row) => {
    if (row?.responded) {
      enqueueSnackbar('Este respondente ya respondió la encuesta', {
        variant: 'info',
        autoHideDuration: 2500,
      });
      return;
    }

    const defaults = await getCurrentSurveyInvitationDefaults();

    setInviteRow(row);
    setInvitationForm({
      emailMask: defaults.emailMask,
      emailSubject: defaults.emailSubject,
      message: defaults.message,
    });
  };

  const closeInvitationDialog = (force = false) => {
    if (sendingInvite && !force) return;
    setInviteRow(null);
    setInvitationForm(emptyInvitationForm);
  };

  const handleFormChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSave = async () => {
    if (!editRow?.respondentId) {
      enqueueSnackbar('Este registro antiguo no tiene RespondentId para modificar', {
        variant: 'warning',
        autoHideDuration: 2500,
      });
      return;
    }

    setSaving(true);
    try {
      await client.put(`respondents/${editRow.respondentId}`, {
        cedula: form.cedula || null,
        email: form.email || null,
        celular: form.celular || null,
        indicativo: form.indicativo === '' ? null : Number(form.indicativo),
        uidEmpresa: form.uidEmpresa || null,
        uid2: form.uid2 || null,
        uid3: form.uid3 || null,
      });

      enqueueSnackbar('Respondente actualizado', {
        variant: 'success',
        autoHideDuration: 2200,
      });

      handleCloseEdit();
      await fetchRespondents();
    } catch (error) {
      const message =
        error?.response?.data ||
        'No se pudo actualizar el respondente';
      enqueueSnackbar(String(message), {
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const messageError =
    inviteRow && !getPlainText(invitationForm.message)
      ? 'El mensaje es requerido.'
      : inviteRow && !PLACEHOLDER_RE.test(invitationForm.message || '')
        ? 'El mensaje debe contener un marcador tipo "@enlace@".'
        : '';
  const emailMaskError =
    inviteRow && !invitationForm.emailMask?.trim()
      ? 'La máscara del correo es requerida.'
      : inviteRow && invitationForm.emailMask.trim().length < 5
        ? 'Mínimo 5 caracteres.'
        : inviteRow && invitationForm.emailMask.trim().length > 30
          ? 'Máximo 30 caracteres.'
          : '';
  const emailSubjectError =
    inviteRow && !invitationForm.emailSubject?.trim()
      ? 'El asunto es requerido.'
      : inviteRow && invitationForm.emailSubject.trim().length < 5
        ? 'Mínimo 5 caracteres.'
        : '';

  const handleInvitationFormChange = (field) => (eventOrValue) => {
    const value = typeof eventOrValue === 'string'
      ? eventOrValue
      : eventOrValue.target.value;

    setInvitationForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSendInvitation = async () => {
    if (!inviteRow || messageError || emailMaskError || emailSubjectError) return;

    setSendingInvite(true);
    try {
      const response = await dispatchSingleAnswerMailAPI(inviteRow.answerId, {
        SurveyId: Number(surveyId),
        CompanyId: companyId,
        Tipo: 'Envio',
        RequestId: createRequestId(),
        EmailMask: invitationForm.emailMask,
        EmailSubject: invitationForm.emailSubject,
        Message: invitationForm.message,
      });

      const result = response?.data?.resultado ?? response?.data?.Resultado ?? response?.data;
      const sent = result?.enviados ?? result?.Enviados ?? 0;
      const noEmail = result?.sinCorreo ?? result?.SinCorreo ?? 0;

      if (sent > 0) {
        enqueueSnackbar('Invitación enviada al correo actualizado', {
          variant: 'success',
          autoHideDuration: 2500,
        });
      } else if (noEmail > 0) {
        enqueueSnackbar('No hay correo válido para enviar la invitación', {
          variant: 'warning',
          autoHideDuration: 3500,
        });
      } else {
        enqueueSnackbar('No se envió invitación porque el respondente ya respondió o no está pendiente', {
          variant: 'info',
          autoHideDuration: 3500,
        });
      }

      closeInvitationDialog(true);
      await fetchRespondents();
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.Mensaje ||
          'No se pudo enviar la invitación al correo actualizado',
        { variant: 'error', autoHideDuration: 3500 },
      );
    } finally {
      setSendingInvite(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!canRunDestructiveActions) return;

    const shouldDelete = window.confirm(
      'Esta acción eliminará todos los usuarios y respuestas de la encuesta. ¿Deseas continuar?'
    );
    if (!shouldDelete) return;

    setDeleting(true);
    try {
      await client.delete(`Survey/Users?surveyId=${surveyId}`);
      enqueueSnackbar('Usuarios y respuestas eliminados', {
        variant: 'success',
        autoHideDuration: 2500,
      });
      setPage(0);
      await fetchRespondents();
    } catch (error) {
      enqueueSnackbar('No se pudieron eliminar todos los usuarios', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleClearAnswer = async (row) => {
    if (!canRunDestructiveActions) return;

    const shouldDelete = window.confirm(
      'Esta acción limpiará la respuesta del usuario, pero conservará el usuario en la encuesta. ¿Deseas continuar?'
    );
    if (!shouldDelete) return;

    setDeleting(true);
    try {
      await client.delete(`UserAnswer?idAnswer=${row.answerId}`);
      enqueueSnackbar('Respuesta eliminada', {
        variant: 'success',
        autoHideDuration: 2500,
      });
      await fetchRespondents();
    } catch (error) {
      enqueueSnackbar('No se pudo eliminar la respuesta', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteUser = async (row) => {
    if (!canRunDestructiveActions) return;

    const shouldDelete = window.confirm(
      'Esta acción eliminará el usuario de la encuesta junto con sus respuestas. ¿Deseas continuar?'
    );
    if (!shouldDelete) return;

    setDeleting(true);
    try {
      await client.delete(`UserFomSurvey?idAnswer=${encodeURIComponent(row.answerId)}`);
      enqueueSnackbar('Usuario eliminado de la encuesta', {
        variant: 'success',
        autoHideDuration: 2500,
      });
      await fetchRespondents();
    } catch (error) {
      enqueueSnackbar('No se pudo eliminar el usuario', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleExportRespondents = async () => {
    if (!surveyId || exporting) return;

    setExporting(true);
    try {
      const pageSize = 500;
      let currentPage = 1;
      let totalPages = 1;
      const allRows = [];

      do {
        const response = await client.get(`surveys/${surveyId}/respondents`, {
          params: {
            status: 'all',
            search: '',
            page: currentPage,
            pageSize,
          },
        });

        const data = response.data || {};
        allRows.push(...(data.items || []));
        totalPages = data.totalPages || Math.ceil((data.totalItems || 0) / pageSize) || 1;
        currentPage += 1;
      } while (currentPage <= totalPages);

      if (allRows.length === 0) {
        enqueueSnackbar('No hay respondentes para exportar', {
          variant: 'info',
          autoHideDuration: 2500,
        });
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(allRows.map(buildExcelRow));
      worksheet['!cols'] = [
        { wch: 38 },
        { wch: 34 },
        { wch: 18 },
        { wch: 18 },
        { wch: 22 },
        { wch: 16 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Respondentes');
      XLSX.writeFile(
        workbook,
        `respondentes_encuesta_${surveyId}_${new Date().toISOString().slice(0, 10)}.xlsx`
      );

      enqueueSnackbar('Excel de respondentes descargado', {
        variant: 'success',
        autoHideDuration: 2500,
      });
    } catch (error) {
      enqueueSnackbar('No se pudo descargar el Excel de respondentes', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            height: 'calc(100vh - 48px)',
            maxHeight: 'calc(100vh - 48px)',
          },
        }}
      >
        <DialogTitle sx={{ px: 3, py: 2.25 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Respondentes
              </Typography>
            </Box>
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ ml: { sm: 'auto' } }}
            >
              <Chip
                label={`${totalResponded} respondieron`}
                size="small"
                sx={{
                  bgcolor: '#e7f5ec',
                  color: '#176c37',
                  border: '1px solid #9bd3ad',
                  fontWeight: 700,
                }}
              />
              <Chip
                label={`${totalPending} no respondieron`}
                size="small"
                sx={{
                  bgcolor: '#fdeaea',
                  color: '#b42318',
                  border: '1px solid #f3a7a3',
                  fontWeight: 700,
                }}
              />
              <Chip
                label={`${totalResponded + totalPending} total`}
                size="small"
                sx={{
                  bgcolor: '#eef2f6',
                  color: '#344054',
                  border: '1px solid #d0d5dd',
                  fontWeight: 700,
                }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={status}
                  onChange={(event) => handleStatusChange(event, event.target.value)}
                  displayEmpty
                  sx={{
                    height: 32,
                    borderRadius: 4,
                    bgcolor: '#f8fafc',
                    fontSize: 13,
                    fontWeight: 700,
                    color: '#344054',
                    '& .MuiSelect-select': {
                      py: 0.5,
                      pl: 1.5,
                    },
                  }}
                >
                  {STATUS_TABS.map((tab) => (
                    <MenuItem key={tab.value} value={tab.value}>
                      {tab.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="Cerrar">
                <IconButton aria-label="Cerrar" onClick={onClose} size="small">
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            minHeight: 560,
            bgcolor: '#f7f8fa',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Stack spacing={1.5} sx={{ minHeight: 0, flex: 1 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
              <TextField
                fullWidth
                placeholder="Buscar por correo, documento, celular o UID"
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              </Stack>
              <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={1}
                sx={{ mt: 1.25 }}
                flexWrap="wrap"
                useFlexGap
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    exporting ? (
                      <CircularProgress size={16} />
                    ) : (
                      <FileDownloadIcon />
                    )
                  }
                  onClick={handleExportRespondents}
                  disabled={
                    exporting || loading || totalResponded + totalPending === 0
                  }
                  sx={{
                    borderRadius: 4,
                    fontWeight: 700,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Descargar Excel
                </Button>
                {canRunDestructiveActions && (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={handleDeleteAll}
                    disabled={deleting || totalResponded + totalPending === 0}
                    sx={{
                      borderRadius: 4,
                      fontWeight: 700,
                      textTransform: 'none',
                    }}
                  >
                    Eliminar todos
                  </Button>
                )}
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1, pl: 0.25 }}
              >
                Mostrando {statusCountsLabel.toLowerCase()} con los filtros actuales.
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                overflow: 'hidden',
                minHeight: 0,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <TableContainer sx={{ minHeight: 0, flex: 1, overflow: 'auto' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        'Correo / identificador',
                        'Documento',
                        'Celular',
                        'UID empresa',
                        'Uid2',
                        'Uid3',
                        'Estado',
                        canRunDestructiveActions ? 'Acciones' : 'Editar',
                      ].map((header, index) => (
                        <TableCell
                          key={header}
                          align={index === 7 ? 'center' : 'left'}
                          sx={{
                            bgcolor: '#f0f3f7',
                            color: 'text.secondary',
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: 0,
                            textTransform: 'uppercase',
                            py: 1.25,
                          }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ height: 300 }}>
                          <CircularProgress size={32} />
                        </TableCell>
                      </TableRow>
                    )}

                    {!loading &&
                      rows.map((row) => {
                        const statusChip = getStatusChipStyles(row.responded);
                        const primaryIdentifier = getPrimaryIdentifier(row);

                        return (
                          <TableRow
                            key={row.answerId}
                            hover
                            sx={{
                              bgcolor: row.responded ? 'background.paper' : '#fff7f7',
                              '& td:first-of-type': {
                                borderLeft: '4px solid',
                                borderLeftColor: row.responded ? 'transparent' : '#d92d20',
                              },
                            }}
                          >
                            <TableCell sx={{ py: 1.25 }}>
                              <Stack spacing={0.25}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {primaryIdentifier}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>{row.cedula || '-'}</TableCell>
                            <TableCell>
                              {[row.indicativo, row.celular].filter(Boolean).join(' ') || '-'}
                            </TableCell>
                            <TableCell>{row.uidEmpresa || '-'}</TableCell>
                            <TableCell>{row.uid2 || '-'}</TableCell>
                            <TableCell>{row.uid3 || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={statusChip.label}
                                variant="outlined"
                                sx={statusChip.sx}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Stack direction="row" spacing={0.5} justifyContent="center">
                                <Tooltip
                                  title={
                                    row.respondentId
                                      ? 'Editar respondente'
                                      : 'Registro antiguo sin RespondentId'
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={!row.respondentId || deleting}
                                      onClick={() => handleOpenEdit(row)}
                                      sx={{
                                        bgcolor: 'action.hover',
                                        '&:hover': { bgcolor: 'primary.50' },
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>

                                <Tooltip
                                  title={
                                    row.responded
                                      ? 'Este respondente ya respondió'
                                      : 'Enviar correo'
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={row.responded || deleting || sendingInvite}
                                      onClick={() => openInvitationDialog(row)}
                                      sx={{
                                        bgcolor: '#eaf7fd',
                                        color: '#0284b8',
                                        '&:hover': { bgcolor: '#d6f0fb' },
                                      }}
                                    >
                                      <EmailOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>

                                {canRunDestructiveActions && (
                                  <>
                                    <Tooltip title="Limpiar respuesta">
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={!row.responded || deleting}
                                          onClick={() => handleClearAnswer(row)}
                                          sx={{
                                            bgcolor: '#fff4e5',
                                            color: '#b54708',
                                            '&:hover': { bgcolor: '#ffead5' },
                                          }}
                                        >
                                          <CleaningServicesIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Eliminar usuario de la encuesta">
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={deleting}
                                          onClick={() => handleDeleteUser(row)}
                                          sx={{
                                            bgcolor: '#fdeaea',
                                            color: '#b42318',
                                            '&:hover': { bgcolor: '#ffd6d2' },
                                          }}
                                        >
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}

                    {!loading && rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ height: 300 }}>
                          <Typography sx={{ fontWeight: 600 }}>
                            No hay respondentes para mostrar.
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Ajusta el filtro o cambia el texto de búsqueda.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={totalItems}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 50]}
                labelRowsPerPage="Filas por pagina"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `mas de ${to}`}`
                }
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  flexShrink: 0,
                }}
              />
            </Paper>
            </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editRow)} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Editar respondente</DialogTitle>
        <DialogContent dividers>
          {!editRow?.respondentId && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Este registro viene del flujo antiguo y no tiene RespondentId.
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Correo"
              value={form.email}
              onChange={handleFormChange('email')}
              fullWidth
            />
            <TextField
              label="Documento"
              value={form.cedula}
              onChange={handleFormChange('cedula')}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Indicativo"
                value={form.indicativo}
                onChange={handleFormChange('indicativo')}
                type="number"
                fullWidth
              />
              <TextField
                label="Celular"
                value={form.celular}
                onChange={handleFormChange('celular')}
                fullWidth
              />
            </Stack>
            <TextField
              label="UID empresa"
              value={form.uidEmpresa}
              onChange={handleFormChange('uidEmpresa')}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Uid2"
                value={form.uid2}
                onChange={handleFormChange('uid2')}
                fullWidth
              />
              <TextField
                label="Uid3"
                value={form.uid3}
                onChange={handleFormChange('uid3')}
                fullWidth
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !editRow?.respondentId}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(inviteRow)} onClose={closeInvitationDialog} maxWidth="md" fullWidth>
        <DialogTitle>Enviar correo al respondente</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Se enviará únicamente a {inviteRow?.email || 'este respondente'}.
            </Typography>
            <TextField
              label="Máscara del correo"
              fullWidth
              size="small"
              value={invitationForm.emailMask}
              error={!!emailMaskError}
              helperText={emailMaskError || 'Nombre que verá el destinatario como remitente.'}
              onChange={handleInvitationFormChange('emailMask')}
            />
            <TextField
              label="Asunto del correo"
              fullWidth
              size="small"
              value={invitationForm.emailSubject}
              error={!!emailSubjectError}
              helperText={emailSubjectError || 'Asunto que recibirá el destinatario.'}
              onChange={handleInvitationFormChange('emailSubject')}
            />
            <Box>
              <Typography
                variant="body2"
                sx={{
                  mb: 0.75,
                  color: messageError ? 'error.main' : 'text.secondary',
                  fontWeight: 600,
                }}
              >
                Mensaje
              </Typography>
              <ReactQuill
                theme="snow"
                modules={quillModules}
                value={invitationForm.message}
                onChange={handleInvitationFormChange('message')}
                placeholder="Escribe el mensaje para el respondente..."
              />
              <Typography
                variant="caption"
                sx={{ color: messageError ? 'error.main' : 'text.secondary' }}
              >
                {messageError || '@enlace@ será reemplazado por el link de la encuesta.'}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeInvitationDialog} disabled={sendingInvite}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSendInvitation}
            disabled={
              sendingInvite ||
              !!messageError ||
              !!emailMaskError ||
              !!emailSubjectError
            }
          >
            {sendingInvite ? 'Enviando...' : 'Enviar invitación'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
